/**
 * Artie Animation System
 * Core engine for scroll-reveals and parallax effects.
 * Optimized for performance using IntersectionObserver and requestAnimationFrame.
 */

class ArtieAnimations {
  constructor() {
    this.revealSelector = '.reveal, .reveal-fade, .reveal-scale';
    this.parallaxSelector = '.parallax-bg';
    this._revealObserver = null;
    this.init();
  }

  init() {
    const runReveals = () => {
      this.initReveals();
      this.initParallax();
      this.initHeroEntrance();
    };

    // Immediate check
    this.initHeroEntrance();

    if (document.readyState === 'complete') {
      runReveals();
    } else {
      window.addEventListener('load', runReveals);
      document.addEventListener('DOMContentLoaded', runReveals);
    }
    
    // Safety check: ensure everything is visible after 3s if JS fails
    setTimeout(() => {
      const heroes = document.querySelectorAll('.artie-hero:not(.loaded)');
      heroes.forEach(hero => hero.classList.add('loaded'));
    }, 3000);
    
    // Shopify Theme Editor Support
    document.addEventListener('shopify:section:load', () => {
      this.initReveals();
      this.initParallax();
    });

    document.addEventListener('shopify:section:select', (event) => {
      // Force visible on selection for better merchant experience
      const reveals = event.target.querySelectorAll(this.revealSelector);
      reveals.forEach(el => el.classList.add('visible'));
    });

    // Support for custom events
    document.addEventListener('artie:animations:reinit', () => {
      this.initReveals();
      this.initParallax();
    });
  }

  /**
   * Catches above-the-fold / full-bleed hero (and similar) when IO reports false
   * because of subpixel, sticky layout, or stricter margin rules in some browsers.
   */
  #revealInViewportFallback() {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    document.querySelectorAll(this.revealSelector).forEach((el) => {
      if (el.classList.contains('visible')) return;
      const r = el.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) return;
      const h = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      const w = Math.min(r.right, vw) - Math.max(r.left, 0);
      if (h <= 0 || w <= 0) return;
      const area = h * w;
      const elArea = Math.max(r.width * r.height, 1);
      if (area / elArea > 0.01 || area > 4) {
        el.classList.add('visible');
      }
    });
  }

  /**
   * Intersection Observer for Reveal animations
   */
  initReveals() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }

    const revealElements = document.querySelectorAll(this.revealSelector);
    if (revealElements.length === 0) return;

    // Do not use negative rootMargin bottom: it shrinks the effective viewport
    // and can prevent full-screen hero / bottom-aligned CTA from ever "intersecting"
    // (e.g. with sticky first section or 100svh blocks).
    const observerOptions = {
      threshold: 0.05,
      rootMargin: '0px 0px 0px 0px'
    };

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    this._revealObserver = revealObserver;

    revealElements.forEach((el) => {
      if (!el.classList.contains('visible')) {
        revealObserver.observe(el);
      }
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#revealInViewportFallback();
      });
    });
  }

  /**
   * Parallax Scroll Listener
   */
  initParallax() {
    const parallaxElements = document.querySelectorAll(this.parallaxSelector);
    if (parallaxElements.length === 0) return;

    let ticking = false;
    const updateParallax = () => {
      const vh = window.innerHeight;
      parallaxElements.forEach(el => {
        const container = el.closest('.parallax-container') || el.parentElement;
        const rect = container.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.15;

        if (rect.bottom > 0 && rect.top < vh) {
          const progress = (rect.top + rect.height) / (vh + rect.height);
          const offset = (progress - 0.5) * rect.height * speed;
          el.style.transform = `translateY(${offset}px)`;
        }
      });
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
    
    updateParallax();
  }

  initHeroEntrance() {
    const heroes = document.querySelectorAll('.artie-hero');
    heroes.forEach(hero => hero.classList.add('loaded'));
  }
}

// Global initialization
window.ArtieAnimations = new ArtieAnimations();

export default ArtieAnimations;
